import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Tooltip from '@mui/material/Tooltip';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
// import DiamondIcon from '@mui/icons-material/Diamond';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import { useTheme } from '@mui/material';
import { Task } from '../../lib/interfaces/Task';
import useUserMeQuery from '../../reactQuery/hooks/useUserMeQuery';
import { IEntityContentField } from '../../lib/interfaces/Common';
import useGetMetadataQuery from '../../reactQuery/hooks/useGetMetadata';
import './TasksSidebarListItem.scss';
import { themeCustomValues } from '../../styles/theme';

type TasksLayoutItemProps = {
	task: Task;
	courseSlug: string;
	currentTask: string;
};

const TasksSidebarListItem: FC<TasksLayoutItemProps> = ({
	task,
	courseSlug,
	currentTask,
}) => {
	const { i18n, t } = useTranslation();
	// const theme = useTheme();
	const navigate = useNavigate();
	const { data: currentUser } = useUserMeQuery();
	const { data: metadata } = useGetMetadataQuery();
	const { slug, subscriptionPlansIds } = task;
	const currentLanguage: string = i18n.language;
	const contentFields: IEntityContentField[] = task.content[currentLanguage];
	const goToTask = () => {
		navigate(`/courses/${courseSlug}/${slug}`);
	};
	const getTaskName = () => contentFields[0].name;
	const isTaskCompleted = currentUser?.completedTasks?.find(
		(item) => item.id === task.id,
	);

	const parentClassNames = classNames({
		TasksSidebarListItem__current: slug === currentTask,
	});

	const getPaidSubscriptionTooltip = () => {
		if (metadata) {
			if (
				currentUser &&
				task.subscriptionPlansIds.includes(currentUser.subscriptionPlanId)
			) {
				return false;
			}
			const { subscriptionPlans } = metadata;
			const taskPlans: string[] = [];
			if (!subscriptionPlansIds.length) {
				return false;
			}
			subscriptionPlansIds.forEach((id) => {
				const assignedPlan = subscriptionPlans.find((plan) => plan.id === id);
				if (assignedPlan) {
					const name = assignedPlan.content[currentLanguage].find(
						(field) => field.label === 'Name',
					);
					if (name?.content) {
						taskPlans.push(name.content);
					}
				}
			});
			if (!taskPlans.length || taskPlans.includes('free')) {
				return false;
			}
			if (taskPlans.length === 1) {
				return t('forSubscribersOnly', { plans: taskPlans[0] });
			}
			if (taskPlans.length === 2) {
				const joinKey = t('and');
				return t('forSubscribersOnly', { plans: taskPlans.join(` ${joinKey} `) });
			}
			return t('forSubscribersOnly', { plans: taskPlans.join(', ') });
		}
		return false;
	};

	const paidSubscriptionTooltip = getPaidSubscriptionTooltip();

	return (
		<ListItem
			key={task.id}
			disablePadding
			onClick={goToTask}
			className={parentClassNames}
			style={{
				borderRadius: themeCustomValues.borderRadius,
			}}
		>
			<ListItemButton style={{ borderRadius: themeCustomValues.borderRadius }}>
				<ListItemIcon
					sx={{
						minWidth: '25px',
					}}
				>
					{!!isTaskCompleted && (
						<CheckCircleIcon sx={{ fontSize: '15px' }} color="secondary" />
					)}
				</ListItemIcon>
				{task.customClass?.includes('cwt-menu-child') && (
					<ListItemIcon
						sx={{
							minWidth: '25px',
						}}
					>
						<SubdirectoryArrowRightIcon sx={{ fontSize: '15px' }} color="primary" />
					</ListItemIcon>
				)}
				<ListItemText
					primary={getTaskName()}
					className="TasksSidebarListItem__text"
				/>
				{paidSubscriptionTooltip && (
					<Tooltip title={paidSubscriptionTooltip}>
						<StarIcon sx={{ fontSize: '20px' }} color="secondary" />
					</Tooltip>
				)}
			</ListItemButton>
		</ListItem>
	);
};

export default TasksSidebarListItem;
